import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { colors } from "@tabbio/tokens";
import { useUaePassStatus } from "../../hooks/useUaePassStatus";
import { useSettingsModal } from "../../providers/useSettingsModal";
import {
  formatCvLastUpdatedDate,
  formatTabbioJoinedDate,
  formatUaePassVerificationDate,
} from "../../lib/uae-pass-verification-details";
import { SocialLoginButton } from "../auth/SocialLoginButton";
import { NativeButton } from "../common/NativeButton";
import { UaePassVerifiedBadge } from "./UaePassVerifiedBadge";

interface UaePassVerificationContentProps {
  enabled?: boolean;
  onClose: () => void;
}

export function UaePassVerificationContent({
  enabled = true,
  onClose,
}: UaePassVerificationContentProps) {
  const { t, i18n } = useTranslation("seeker");
  const settingsModal = useSettingsModal();
  const status = useUaePassStatus(enabled);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Linking lives in Settings > Data management. Open the settings dialog on
  // that category in place of the host dialog rather than leaving the page;
  // native keeps pushing the route (UaePassVerificationContent.tsx).
  const continueWithUaePass = () => {
    onClose();
    settingsModal.open({ category: "dataManagement" });
  };

  if (status.isLoading && !status.data) {
    return (
      <div
        role="status"
        aria-label={t("common:status.loading", { defaultValue: "Loading" })}
        style={loadingStyle}
      >
        <span style={spinnerStyle} />
      </div>
    );
  }

  if (status.isError && !status.data) {
    return (
      <div style={errorStyle}>
        <p style={bodyStyle}>{t("jobs.uaePass.loadFailed")}</p>
        <NativeButton
          title={t("common:actions.retry")}
          onPress={() => void status.refetch()}
          color="contrast"
          size="lg"
          pill
          block
        />
      </div>
    );
  }

  const isLinked = status.data?.isLinked === true;
  const verifiedDate = formatUaePassVerificationDate(
    status.data?.linkedAt,
    i18n.language,
  );
  const joinedDate = formatTabbioJoinedDate(
    status.data?.joinedAt,
    i18n.language,
  );
  const cvUpdatedDate = formatCvLastUpdatedDate(
    status.data?.cvUpdatedAt,
    i18n.language,
  );

  if (isLinked) {
    return (
      <div style={linkedStyle}>
        <div style={verifiedHeadingStyle}>
          <UaePassVerifiedBadge size={28} color="currentColor" />
          <h3 style={verifiedTitleStyle}>{t("jobs.uaePass.verifiedTitle")}</h3>
        </div>
        <p style={verifiedSubtitleStyle}>
          {t("jobs.uaePass.governmentIdentity")}
          {verifiedDate ? ` · ${verifiedDate}` : ""}
        </p>

        <div style={timelineStyle}>
          <div>
            <div style={timelineLabelStyle}>{t("jobs.uaePass.onTabbio")}</div>
            <div style={timelineValueStyle}>
              {joinedDate
                ? t("jobs.uaePass.since", { date: joinedDate })
                : t("jobs.uaePass.dateUnavailable")}
            </div>
          </div>
          <div>
            <div style={timelineLabelStyle}>
              {t("jobs.uaePass.cvLastUpdated")}
            </div>
            <div style={timelineValueStyle}>
              {cvUpdatedDate || t("jobs.uaePass.notUpdatedYet")}
            </div>
          </div>
        </div>

        {showHowItWorks ? (
          <div style={detailsStyle}>
            <p style={bodyStyle}>{t("jobs.uaePass.verifiedBody")}</p>
            <BenefitList />
          </div>
        ) : null}

        <div style={footerStyle}>
          <NativeButton
            title={
              showHowItWorks
                ? t("jobs.uaePass.hideHowItWorks")
                : t("jobs.uaePass.howItWorks")
            }
            onPress={() => setShowHowItWorks((current) => !current)}
            variant="ghost"
            size="md"
            block
            textColor={colors.primary}
          />
          <NativeButton
            title={t("common:actions.done")}
            onPress={onClose}
            color="contrast"
            size="lg"
            pill
            block
          />
        </div>
      </div>
    );
  }

  return (
    <div style={unlinkedStyle}>
      <div style={heroStyle}>
        <UaePassVerifiedBadge size={30} />
        <h3 style={titleStyle}>{t("jobs.uaePass.unverifiedTitle")}</h3>
        <p style={bodyStyle}>{t("jobs.uaePass.body")}</p>
      </div>
      <BenefitList />
      <SocialLoginButton
        provider="uae-pass"
        label={t("jobs.uaePass.cta")}
        onPress={continueWithUaePass}
      />
    </div>
  );
}

function BenefitList() {
  const { t } = useTranslation("seeker");

  return (
    <div style={bulletsStyle}>
      {[
        t("jobs.uaePass.benefitBadge"),
        t("jobs.uaePass.benefitName"),
        t("jobs.uaePass.benefitPrivacy"),
      ].map((label) => (
        <div key={label} style={bulletStyle}>
          <Check size={15} strokeWidth={2.1} style={{ flexShrink: 0 }} />
          <span style={bulletTextStyle}>{label}</span>
        </div>
      ))}
    </div>
  );
}

const loadingStyle: CSSProperties = {
  minHeight: 180,
  display: "grid",
  placeItems: "center",
};

const spinnerStyle: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  border: "2px solid var(--input-border)",
  borderTopColor: "var(--text-secondary)",
  animation: "tabbio-spinner-spin 0.8s linear infinite",
};

const errorStyle: CSSProperties = {
  display: "grid",
  gap: 16,
};

const linkedStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  color: "var(--text-primary)",
};

const verifiedHeadingStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const verifiedTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 21,
  lineHeight: "28px",
  fontWeight: 700,
};

const verifiedSubtitleStyle: CSSProperties = {
  margin: "4px 0 0",
  color: "var(--text-secondary)",
  fontSize: 15,
  lineHeight: "22px",
};

const timelineStyle: CSSProperties = {
  display: "grid",
  gap: 20,
  padding: "22px 0 18px",
};

const timelineLabelStyle: CSSProperties = {
  fontSize: 16,
  lineHeight: "22px",
  fontWeight: 700,
};

const timelineValueStyle: CSSProperties = {
  marginTop: 2,
  color: "var(--text-secondary)",
  fontSize: 15,
  lineHeight: "22px",
};

const detailsStyle: CSSProperties = {
  display: "grid",
  gap: 12,
  paddingTop: 8,
};

const footerStyle: CSSProperties = {
  display: "grid",
  gap: 8,
  marginTop: 8,
  paddingTop: 8,
  borderTop: "1px solid var(--input-border)",
};

const unlinkedStyle: CSSProperties = {
  display: "grid",
  gap: 8,
};

const heroStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  paddingInline: 16,
  textAlign: "center",
  color: "var(--text-primary)",
};

const titleStyle: CSSProperties = {
  margin: "4px 0 0",
  fontSize: 17,
  fontWeight: 700,
};

const bodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: "20px",
  color: "var(--text-secondary)",
  textAlign: "center",
};

const bulletsStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  paddingInline: 8,
  margin: "12px 0",
  color: "var(--text-secondary)",
};

const bulletStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const bulletTextStyle: CSSProperties = {
  flex: 1,
  fontSize: 12,
  lineHeight: "18px",
};
